import { IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import {
  ConformationStream,
  IBreedBucket,
  IPartitionedScores,
} from "./types";

const ELEMENT = Constants.dog_class_element;
const LEVEL = Constants.dog_class_element_level;

export const GROUP_LABELS: Record<string, string> = {
  Guardian: "Guardian Dogs",
  Scenthound: "Scenthounds",
  "Sighthound & Pariah Dog": "Sighthound & Pariah Dogs",
  "Gun Dog": "Gun Dogs",
  "Northern Breed": "Northern Breeds",
  "Herding Dogs": "Herding Dogs",
  "Terrier Breeds": "Terriers",
  "Companion Dogs": "Companion Dogs",
};

export function groupLabel(stored: string): string {
  return GROUP_LABELS[stored] || stored;
}

export function sortGroups(groups: string[]): string[] {
  const order: string[] = Constants.ukc_breed_group_order || [];
  return [
    ...order.filter((g) => groups.includes(g)),
    ...groups.filter((g) => !order.includes(g)).sort(),
  ];
}

function levelOf(score: IScore): string {
  return score.dog_class?.level || "";
}

function elementOf(score: IScore): string {
  return score.dog_class?.show_element || "";
}

export function isNovicePuppy(score: IScore): boolean {
  const level = levelOf(score);
  const element = elementOf(score);
  return (
    level === LEVEL.novice_puppy ||
    level === ELEMENT.novice_puppy ||
    element === ELEMENT.novice_puppy
  );
}

export function isVeteran(score: IScore): boolean {
  const level = levelOf(score);
  const element = elementOf(score);
  return level === LEVEL.veteran || level === ELEMENT.veteran || element === ELEMENT.veteran;
}

export function isBrace(score: IScore): boolean {
  return levelOf(score) === LEVEL.brace || elementOf(score) === ELEMENT.brace;
}

/**
 * Split a show's conformation scores into entry streams. Total Junior is dropped
 * (no result entry); Pee Wee / Sub-Junior stay in `junior` so the section knows
 * they exist even though they get no placement controls.
 */
export function partitionScores(scores: IScore[]): IPartitionedScores {
  const regular: IScore[] = [];
  const altered: IScore[] = [];
  const novicePuppy: IScore[] = [];
  const veteran: IScore[] = [];
  const brace: IScore[] = [];
  const junior: IScore[] = [];

  scores.forEach((score) => {
    const element = elementOf(score);
    const level = levelOf(score);

    if (element === ELEMENT.junior_showmanship || element === ELEMENT.conformation_junior) {
      if (level !== LEVEL.total_junior && level !== ELEMENT.total_junior) junior.push(score);
      return;
    }

    if (element === ELEMENT.conformation_altered) {
      altered.push(score);
      return;
    }

    if (element === ELEMENT.conformation) {
      regular.push(score);
      return;
    }

    if (isBrace(score)) {
      brace.push(score);
      return;
    }

    if (isNovicePuppy(score)) {
      novicePuppy.push(score);
      return;
    }

    if (isVeteran(score)) {
      veteran.push(score);
    }
  });

  return { regular, altered, novicePuppy, veteran, brace, junior };
}

/** Scores for one stream from a partition result. */
export function streamScores(parts: IPartitionedScores, stream: ConformationStream): IScore[] {
  switch (stream) {
    case "regular":
      return parts.regular;
    case "altered":
      return parts.altered;
    case "novice_puppy":
      return parts.novicePuppy;
    case "veteran":
      return parts.veteran;
    case "brace":
      return parts.brace;
    default:
      return [];
  }
}

/** breed_group → breeds (sorted by name) → scores. */
export function groupByBreed(scores: IScore[]): Map<string, IBreedBucket[]> {
  const map = new Map<string, IBreedBucket[]>();
  scores.forEach((score) => {
    const breedGroup = score.breed_group || "Unknown";
    const breed = score.breed || "Unknown";
    if (!map.has(breedGroup)) map.set(breedGroup, []);
    const breeds = map.get(breedGroup)!;
    let bucket = breeds.find((b) => b.breed === breed);
    if (!bucket) {
      bucket = { breed, breedGroup, scores: [] };
      breeds.push(bucket);
    }
    bucket.scores.push(score);
  });
  map.forEach((breeds) => breeds.sort((a, b) => a.breed.localeCompare(b.breed)));
  return map;
}

/** breed_group → scores, for the Groups section (Regular only by convention). */
export function groupByBreedGroup(scores: IScore[]): Map<string, IScore[]> {
  const byGroup = new Map<string, IScore[]>();
  scores.forEach((score) => {
    const group = score.breed_group || "Unknown";
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group)!.push(score);
  });
  return byGroup;
}
